/**
 * CSRF Protection Utility
 *
 * Reads CSRF tokens from cookies set by Spring Security.
 * The backend sets an XSRF-TOKEN cookie, which must be sent back
 * in the X-XSRF-TOKEN header for state-changing operations.
 *
 * This works alongside Auth0 bearer tokens for defense-in-depth security.
 */

const CSRF_COOKIE_NAME = 'XSRF-TOKEN';
const CSRF_HEADER_NAME = 'X-XSRF-TOKEN';

/**
 * Get the CSRF token from cookies (set by Spring Security)
 */
export function getCsrfToken(): string {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  // Token not found - backend should set it on first request
  return '';
}

/**
 * Clear the CSRF token (call on logout)
 * Note: The cookie is httpOnly=false so we can read it, but
 * the server manages its lifecycle
 */
export function clearCsrfToken(): void {
  // Cookie is managed by the server, nothing to clear client-side
}

/**
 * Get the CSRF header name for use in requests
 */
export function getCsrfHeaderName(): string {
  return CSRF_HEADER_NAME;
}

/**
 * Get CSRF headers object for use with axios/fetch
 */
export function getCsrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  if (token) {
    return {
      [CSRF_HEADER_NAME]: token
    };
  }
  return {};
}

/**
 * Check if a request method requires CSRF protection
 */
export function requiresCsrfProtection(method: string): boolean {
  const protectedMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
  return protectedMethods.includes(method.toUpperCase());
}
